import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select } from 'antd';
import { useAuth } from '../../context/authContext';
import { UserGroup } from '../../pages/UserGroupsPage';
import {
  OverlayMain,
  OverlaySelect,
  StyledSelect,
  HeaderBoxCenter,
  Field,
  ChildrenContainer,
} from './styles';

interface UsergroupBarProps {
  children?: React.ReactNode;
  barContent?: React.ReactNode;
  shouldHighlightUserGroups?: boolean;
  onChangeUsergroup?: () => void;
  updateBreadcrumb?: Function;
  disabled?: boolean;
}

const UsergroupBar: React.FC<UsergroupBarProps> = ({
  children,
  barContent,
  shouldHighlightUserGroups,
  onChangeUsergroup,
  updateBreadcrumb,
  disabled = false,
}) => {
  const authContext = useAuth();
  const { user } = authContext;

  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [selectedCompany, setSelectedCompany] = useState<number>();
  const [isHighlighted, setIsHighlighted] = useState<boolean>(
    shouldHighlightUserGroups ?? false,
  );

  useEffect(() => {
    const time = setTimeout(() => setIsHighlighted(false), 3000);
    return () => clearTimeout(time);
  }, []);

  const uniqueCompanies = useMemo<number[]>(() => {
    return Array.from(
      new Set(user?.usergroups?.map((ug: UserGroup) => ug.companyId) ?? []),
    );
  }, [user.usergroups]);

  const shouldDisplayCompanySelect = uniqueCompanies.length > 1;

  const filteredUserGroups = useMemo<UserGroup[]>(() => {
    return (user.usergroups ?? []).filter(
      (ug: UserGroup) => ug.companyId === selectedCompany,
    );
  }, [user.usergroups, selectedCompany]);

  const handleGroupChange = useCallback(
    (value: any): void => {
      setSelectedGroup(value);

      const usergroup = user.usergroups.find(
        (ug: UserGroup) => ug.userGroupId === parseInt(value, 10),
      );

      authContext.updateSelectedUsergroup(usergroup);

      if (shouldHighlightUserGroups) {
        setIsHighlighted(true);
        setTimeout(() => setIsHighlighted(false), 3000);
      }

      onChangeUsergroup?.();
      updateBreadcrumb?.({}, true);
    },
    [
      authContext,
      user.usergroups,
      shouldHighlightUserGroups,
      onChangeUsergroup,
      updateBreadcrumb,
    ],
  );

  useEffect(() => {
    setSelectedGroup(user.selectedUsergroup?.name ?? '');
    setSelectedCompany(
      user.usergroups?.find(
        (usergroup: UserGroup) =>
          usergroup.userGroupId === user.selectedUsergroup?.userGroupId,
      )?.companyId,
    );
  }, [
    user.selectedUsergroup?.name,
    user.selectedUsergroup?.userGroupId,
    user.usergroups,
  ]);

  const hasMultipleUsergroups = (user.usergroups?.length ?? 0) > 1;

  if (disabled || user.isGuest) {
    return <>{children}</>;
  }

  // With a single usergroup there's nothing to pick, so the bar only needs
  // to render when the page has other filters (barContent) to show.
  if (!hasMultipleUsergroups && !barContent) {
    return <>{children}</>;
  }

  if (hasMultipleUsergroups && !user.selectedUsergroup) {
    return (
      <OverlayMain>
        <OverlaySelect
          value={selectedGroup}
          onChange={e => handleGroupChange(e.target.value)}
        >
          <option value="" disabled>
            Bitte wähle hier das gewünschte Projekt aus.
          </option>
          {user.usergroups.map((usergroup: UserGroup) => (
            <option key={usergroup.userGroupId} value={usergroup.userGroupId}>
              {usergroup.name}
            </option>
          ))}
        </OverlaySelect>
      </OverlayMain>
    );
  }

  return (
    <>
      <HeaderBoxCenter>
        {hasMultipleUsergroups && shouldDisplayCompanySelect && (
          <Field>
            <Select
              placeholder="Select Company"
              value={selectedCompany}
              onChange={value => setSelectedCompany(value)}
            >
              <Select.Option value="" disabled>
                Select Company
              </Select.Option>
              {uniqueCompanies.map((companyId: number) => (
                <Select.Option key={companyId} value={companyId}>
                  {
                    user.usergroups.find((u: any) => u.companyId === companyId)
                      ?.companyName
                  }
                </Select.Option>
              ))}
            </Select>
          </Field>
        )}

        {hasMultipleUsergroups && (
          <Field>
            <StyledSelect
              $isHighlighted={isHighlighted}
              placeholder="Bitte wähle hier das gewünschte Projekt aus."
              value={selectedGroup}
              onChange={handleGroupChange}
              optionLabelProp="label"
            >
              <Select.Option value="" disabled>
                Bitte wähle hier das gewünschte Projekt aus.
              </Select.Option>

              {filteredUserGroups.map((ug: UserGroup) => (
                <Select.Option key={ug.userGroupId} value={ug.userGroupId}>
                  {ug.name}
                </Select.Option>
              ))}
            </StyledSelect>
          </Field>
        )}

        {barContent && (
          <Field>
            <ChildrenContainer>{barContent}</ChildrenContainer>
          </Field>
        )}
      </HeaderBoxCenter>
      {children}
    </>
  );
};

export default UsergroupBar;
